import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  Alert,
  CircularProgress,
  TextField,
  FormHelperText
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomButton from "../../../../components/CustomButton/Button";

interface AddUserDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
  error: string;
  userToAdd: string;
  setUserToAdd: (userToAdd: string) => void;
}

const AddUserDialog: React.FC<AddUserDialogProps> = ({
  open,
  onClose,
  onConfirm,
  loading,
  error,
  userToAdd,
  setUserToAdd
}) => {
  const [validationError, setValidationError] = React.useState("");

  const validateUserId = (userId: string) => {
    // UUID format validation (basic check)
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(userId)) {
      return "Please enter a valid User ID (UUID format)";
    }
    return "";
  };

  const handleConfirm = () => {
    const trimmedUserId = userToAdd.trim();
    if (!trimmedUserId) {
      setValidationError("User ID is required");
      return;
    }

    const validationError = validateUserId(trimmedUserId);
    if (validationError) {
      setValidationError(validationError);
      return;
    }

    setValidationError("");
    onConfirm();
  };

  const handleClose = () => {
    setUserToAdd("");
    setValidationError("");
    onClose();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserToAdd(e.target.value);
    if (validationError) {
      setValidationError("");
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography sx={{ fontWeight: 600, fontSize: "18px" }}>
            Add User to Group
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disabled={loading}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {(error || validationError) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || validationError}
          </Alert>
        )}
        <Typography sx={{ mb: 2 }}>
          Enter the User ID of the user you want to add to this group. The user
          will be added to the group and will appear in the "Associated
          Accounts" section of their profile.
        </Typography>
        <TextField
          fullWidth
          label="User ID"
          value={userToAdd}
          onChange={handleInputChange}
          disabled={loading}
          placeholder="Enter User ID (e.g., b02d3127-1490-4341-ac71-5c8ea27b0dd0)"
          error={!!validationError}
          sx={{ mt: 1 }}
        />
        <FormHelperText sx={{ mt: 1, color: "text.secondary" }}>
          Format: UUID (e.g., xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)
        </FormHelperText>
      </DialogContent>
      <DialogActions>
        <CustomButton callBack={handleClose} disabled={loading}>
          Cancel
        </CustomButton>
        <CustomButton
          callBack={handleConfirm}
          disabled={loading || !userToAdd.trim()}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Add User"
          )}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddUserDialog;
